import { createContext, useContext, type Context } from 'react';

import type { MenuControlContextProps, MenuStateContextProps } from './types';

/**
 * Context for managing section expansion state in the universal create menu
 */
export interface SectionExpansionContextProps {
	sectionExpansionState: Record<number, boolean>;
	setSectionExpansionState: (sectionIndex: number, expanded: boolean) => void;
}

const defaultSectionExpansionContext: SectionExpansionContextProps = {
	sectionExpansionState: {},
	setSectionExpansionState: function (): void {},
};

export const SectionExpansionContext: Context<SectionExpansionContextProps> =
	createContext<SectionExpansionContextProps>(defaultSectionExpansionContext);

export function useSectionExpansionContext(): SectionExpansionContextProps {
	return useContext(SectionExpansionContext);
}

const defaultFormControls: MenuControlContextProps = {
	load: function (): void {},
	reset: function (): void {},
	close: function (): void {},
};

/**
 * Menu Control Context - responsible for controlling the menu, back, cancel and reset to menu list
 */
export const MenuControlContext: Context<MenuControlContextProps> =
	createContext<MenuControlContextProps>(defaultFormControls);

export function useMenuControl(): MenuControlContextProps {
	return useContext(MenuControlContext);
}

/**
 * Menu State Context - responsible for the state of the menu,
 * Including items/sections, product, subProduct, suggestionConfig and other configs.
 */
export const MenuStateContext: Context<MenuStateContextProps> =
	createContext<MenuStateContextProps>({
		isV2Mode: false,
		items: [],
		product: 'unknown',
		subProduct: undefined,
		growthRecommendationsSessionId: undefined,
		setGrowthRecommendationsSessionId: undefined,
		crossAppCreationConfig: {
			enabled: false,
		},
		sections: [],
		suggestionConfig: {
			enabled: false,
		},
		isNav4Enabled: true,
		preventClose: undefined,
		controlledOpen: undefined,
	});

export function useMenuStateContext(): MenuStateContextProps {
	return useContext(MenuStateContext);
}

/**
 * Unified hook for accessing Universal Create state.
 * Reads from MenuStateContext.
 *
 * @returns State properties including product, subProduct, items, sections, etc.
 */
export function useUniversalCreateState(): MenuStateContextProps {
	return useContext(MenuStateContext);
}

/**
 * Unified hook for accessing Universal Create control methods.
 * Reads from MenuControlContext.
 *
 * @returns Control methods including load, reset, and close.
 */
export function useUniversalCreateControl(): MenuControlContextProps {
	return useContext(MenuControlContext);
}
